import React from 'react';

import { Group, Text, UnstyledButton, useMantineTheme } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import Menu from 'components/Menu';

export interface SectionOption {
  key: string;
  label: string;
}

interface WorkoutSectionSelectorProps {
  sections: SectionOption[];
  selectedKey: string | null;
  onSelect: (key: string | null) => void;
}

const FULL_WORKOUT_LABEL = 'Full Workout';

const WorkoutSectionSelector = ({
  sections,
  selectedKey,
  onSelect,
}: WorkoutSectionSelectorProps): JSX.Element | null => {
  const theme = useMantineTheme();

  if (sections.length < 1) return null;

  const allOptions: Array<{ key: string | null; label: string }> = [
    { key: null, label: FULL_WORKOUT_LABEL },
    ...sections.map(s => ({ key: s.key, label: s.label })),
  ];

  const currentIndex = selectedKey === null ? 0 : allOptions.findIndex(o => o.key === selectedKey);
  const currentLabel = allOptions[currentIndex]?.label ?? FULL_WORKOUT_LABEL;

  const goPrev = (): void => {
    const prevIndex = currentIndex <= 0 ? allOptions.length - 1 : currentIndex - 1;
    onSelect(allOptions[prevIndex].key);
  };

  const goNext = (): void => {
    const nextIndex = currentIndex >= allOptions.length - 1 ? 0 : currentIndex + 1;
    onSelect(allOptions[nextIndex].key);
  };

  const menuItems = allOptions.map(option => ({
    title: option.label,
    onClick: () => onSelect(option.key),
  }));

  return (
    <Group spacing='xs' noWrap>
      <UnstyledButton onClick={goPrev} aria-label='Previous section'>
        <IconChevronLeft size={20} color={theme.colors.gray[6]} />
      </UnstyledButton>

      <Menu
        items={menuItems}
        target={
          <UnstyledButton>
            <Group spacing={4} noWrap>
              <Text size='sm' weight={selectedKey === null ? 600 : 400} style={{ whiteSpace: 'nowrap' }}>
                {currentLabel}
              </Text>
              <IconChevronRight
                size={14}
                color={theme.colors.gray[5]}
                style={{ transform: 'rotate(90deg)' }}
              />
            </Group>
          </UnstyledButton>
        }
        position='bottom'
      />

      <UnstyledButton onClick={goNext} aria-label='Next section'>
        <IconChevronRight size={20} color={theme.colors.gray[6]} />
      </UnstyledButton>
    </Group>
  );
};

export default WorkoutSectionSelector;
